import React, { HTMLAttributes } from 'react';
import EmergeLogoComponent from '@/assets/EmergeLogo';
import { ReactComponent as EmergeLogoNavy } from '@/assets/EmergeLogoNavy.svg';

import cn from '@/utils/cn';
import { ArrowUpIcon, ArrowDownIcon, ArrowLeftIcon, ArrowRightIcon } from '@/components/Icons';
import { TransactionDataTypeEnum, TransactionTypeEnum } from '@/services/types/TransactionData';
import { EnumValues } from '@/services/types/EnumValues';
import { CurrencyDollarIcon } from '@/components/Icons';
import { generateRandomString } from '@/utils/generateRandomString';

const IconWrapperClass = cn([
  'flex',
  'w-9',
  'h-9',
  'items-center',
  'justify-center',
  'rounded-full',
  'text-emerge-navy',
  'bg-emerge-teal',
  'm-auto'
]);

const IconWrapper: React.FC<HTMLAttributes<HTMLDivElement>> = ({ className, children, id }) => (
  <div id={id} className={cn(IconWrapperClass, className)}>
    {children}
  </div>
);

export const OutgoingIcon = () => (
  <IconWrapper className="w-8 h-8">
    <ArrowUpIcon size="md" />
  </IconWrapper>
);

export const TransferIcon = () => (
  <IconWrapper className="w-8 h-8 relative">
    <ArrowLeftIcon size="md" className="absolute" />
    <ArrowRightIcon size="md" />
  </IconWrapper>
);

export const IncomingIcon = () => (
  <IconWrapper className="w-8 h-8">
    <ArrowDownIcon size="md" />
  </IconWrapper>
);

export const AdminFeesIcon: React.FC<{ id: string }> = ({ id }) => (
  <IconWrapper className="bg-emerge-navy w-8 h-8">
    <EmergeLogoComponent id={id} width="16" height="16" />
  </IconWrapper>
);

export const TopUpIcon = () => (
  <IconWrapper className="w-8 h-8">
    <CurrencyDollarIcon size="md" />
  </IconWrapper>
);

export const EmergePayIcon = () => (
  <IconWrapper className="w-8 h-8">
    <EmergeLogoNavy width="16" height="16" />
  </IconWrapper>
);

export const getTransactionIcon = (data: {
  transactionType: EnumValues<typeof TransactionTypeEnum>;
  transactionDataType: EnumValues<typeof TransactionDataTypeEnum> | null;
}) => {
  switch (data.transactionType) {
    case TransactionTypeEnum.Bank: {
      if (data.transactionDataType?.toLowerCase() === TransactionDataTypeEnum.Incoming) {
        return <IncomingIcon />;
      }

      return <OutgoingIcon />;
    }
    case TransactionTypeEnum.Purchase: {
      if (data.transactionDataType?.toLowerCase() === TransactionDataTypeEnum.Incoming) {
        return <IncomingIcon />;
      }

      return <OutgoingIcon />;
    }
    case TransactionTypeEnum.Transfer: {
      return <TransferIcon />;
    }
    case TransactionTypeEnum.Admin: {
      return <AdminFeesIcon id={`get-transaction-icon-${generateRandomString()}`} />;
    }
    case TransactionTypeEnum.TopUp: {
      return <TopUpIcon />;
    }
    case TransactionTypeEnum.EmergePay: {
      return <EmergePayIcon />;
    }
  }
};

const SmallIconClass = 'w-2 h-2';
export const getSmallIcon = (data: {
  transactionType: EnumValues<typeof TransactionTypeEnum>;
  transactionDataType: EnumValues<typeof TransactionDataTypeEnum> | null;
}) => {
  switch (data.transactionType) {
    case TransactionTypeEnum.Bank: {
      if (data.transactionDataType === TransactionDataTypeEnum.Incoming) {
        return <ArrowDownIcon variant="input" className={SmallIconClass} />;
      }

      return <ArrowUpIcon variant="input" className={SmallIconClass} />;
    }
    case TransactionTypeEnum.Purchase: {
      if (data.transactionDataType === TransactionDataTypeEnum.Incoming) {
        return <ArrowDownIcon variant="input" className={SmallIconClass} />;
      }

      return <ArrowUpIcon variant="input" className={SmallIconClass} />;
    }
    case TransactionTypeEnum.Transfer: {
      return (
        <>
          <ArrowLeftIcon variant="input" className={cn(SmallIconClass, 'absolute')} />
          <ArrowRightIcon variant="input" className={SmallIconClass} />
        </>
      );
    }
    case TransactionTypeEnum.Admin: {
      return <AdminFeesIcon id={`small-icon-${generateRandomString()}`} />;
    }
    case TransactionTypeEnum.TopUp: {
      return <TopUpIcon />;
    }
    case TransactionTypeEnum.EmergePay: {
      return <EmergePayIcon />;
    }
  }
};
